import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Loader2, Clock, Ban } from 'lucide-react';
import { ImportJob } from '@/types/import';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface ImportProgressProps {
  jobId: string;
  onComplete: (job: ImportJob) => void;
  onCancel: () => void;
  getJobStatus: (jobId: string) => Promise<ImportJob>;
}

export const ImportProgress: React.FC<ImportProgressProps> = ({
  jobId,
  onComplete,
  onCancel,
  getJobStatus,
}) => {
  const [job, setJob] = useState<ImportJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startTime] = useState<Date>(new Date());

  // Poll for job status
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    let mounted = true;

    const fetchStatus = async () => {
      try {
        const status = await getJobStatus(jobId);
        if (mounted) {
          setJob(status);

          // Check if job is complete
          if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(status.status)) {
            clearInterval(intervalId);
            onComplete(status);
          }
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch job status');
        }
      }
    };

    // Initial fetch
    fetchStatus();

    // Poll every 2 seconds
    intervalId = setInterval(fetchStatus, 2000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [jobId, getJobStatus, onComplete]);

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel this import? Progress will be lost.')) {
      onCancel();
    }
  };

  const getProgressPercentage = (): number => {
    if (!job || job.totalRows === 0) return 0;
    return Math.round((job.processedRows / job.totalRows) * 100);
  };

  const getEstimatedTimeRemaining = (): string => {
    if (!job || job.processedRows === 0) return 'Calculating...';

    const elapsedMs = new Date().getTime() - startTime.getTime();
    const rowsPerMs = job.processedRows / elapsedMs;
    const remainingRows = job.totalRows - job.processedRows;
    const remainingMs = remainingRows / rowsPerMs;

    if (remainingMs < 1000) return 'Less than a second';
    if (remainingMs < 60000) return `${Math.round(remainingMs / 1000)} seconds`;
    return `${Math.round(remainingMs / 60000)} minutes`;
  };

  const getStatusIcon = () => {
    if (!job) return <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />;

    switch (job.status) {
      case 'COMPLETED':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'FAILED':
        return <XCircle className="w-6 h-6 text-red-600" />;
      case 'CANCELLED':
        return <Ban className="w-6 h-6 text-gray-600" />;
      default:
        return <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />;
    }
  };

  const getStatusText = () => {
    if (!job) return 'Initializing...';

    switch (job.status) {
      case 'PENDING':
        return 'Preparing import...';
      case 'VALIDATING':
        return 'Validating data...';
      case 'PROCESSING':
        return 'Importing leads...';
      case 'COMPLETED':
        return 'Import completed!';
      case 'FAILED':
        return 'Import failed';
      case 'CANCELLED':
        return 'Import cancelled';
      default:
        return 'Processing...';
    }
  };

  const progress = getProgressPercentage();
  const isRunning = job && ['PENDING', 'VALIDATING', 'PROCESSING'].includes(job.status);

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {getStatusIcon()}
          <div>
            <h4 className="text-lg font-medium text-gray-900">{getStatusText()}</h4>
            {isRunning && (
              <p className="text-sm text-gray-500">
                <Clock className="w-3 h-3 inline mr-1" />
                Estimated time remaining: {getEstimatedTimeRemaining()}
              </p>
            )}
          </div>
        </div>

        {isRunning && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            Cancel Import
          </Button>
        )}
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            {job ? `${job.processedRows.toLocaleString()} of ${job.totalRows.toLocaleString()} rows` : 'Loading...'}
          </span>
          <span className="font-medium text-gray-900">{progress}%</span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-500',
              job?.status === 'COMPLETED' ? 'bg-green-600' :
              job?.status === 'FAILED' ? 'bg-red-600' :
              'bg-blue-600'
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      {job && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{job.successCount.toLocaleString()}</div>
            <div className="text-sm text-green-700">Successful</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{job.failedCount.toLocaleString()}</div>
            <div className="text-sm text-red-700">Failed</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{job.skippedCount.toLocaleString()}</div>
            <div className="text-sm text-yellow-700">Skipped</div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Processing animation */}
      {isRunning && (
        <div className="flex items-center justify-center py-4">
          <div className="flex space-x-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                style={{ animationDelay: `${i * 0.1}s` }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
